import { useCallback, useEffect, useRef } from 'react'

import { pauseVideo, releaseVideo, restoreAndPlay } from '@/utils/video-resource'

const ANCHOR_IDLE_DELAY_MS = 180

type VideoHandle = {
  play: () => void
  pause: () => void
}

type Params = {
  canAutoplay: boolean
  resumeKey?: number
  anchorIdleDelayMs?: number
}

type Return = {
  bindVideoRef: (index: number, el: HTMLVideoElement | null) => void
  onInViewChange: (index: number, inView: boolean) => void
  onEnded: (index: number) => void
  retryActive: () => void
  reset: () => void
}

function makeHandle(video: HTMLVideoElement): VideoHandle {
  return {
    play: () => restoreAndPlay(video),
    pause: () => pauseVideo(video),
  }
}

export function useSequentialVideoController({
  canAutoplay,
  resumeKey,
  anchorIdleDelayMs = ANCHOR_IDLE_DELAY_MS,
}: Params): Return {
  const videoMapRef = useRef<Map<number, HTMLVideoElement>>(new Map())
  const inViewSetRef = useRef<Set<number>>(new Set())
  const activeIndexRef = useRef<number | null>(null)
  const startTimersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map())
  const canAutoplayRef = useRef(canAutoplay)
  canAutoplayRef.current = canAutoplay

  const clearStartTimer = useCallback((index: number) => {
    const t = startTimersRef.current.get(index)
    if (t !== undefined) {
      clearTimeout(t)
      startTimersRef.current.delete(index)
    }
  }, [])

  const clearAllTimers = useCallback(() => {
    startTimersRef.current.forEach((_, idx) => clearStartTimer(idx))
  }, [clearStartTimer])

  const getHandle = useCallback((index: number): VideoHandle | null => {
    const el = videoMapRef.current.get(index)
    return el ? makeHandle(el) : null
  }, [])

  const sortedInView = useCallback((): number[] => {
    return [...inViewSetRef.current].sort((a, b) => a - b)
  }, [])

  const selectAndPlay = useCallback((forceReplayIndex?: number) => {
    if (!canAutoplayRef.current) return

    const sorted = sortedInView()
    const nextActive = sorted[0] ?? null
    const prevActive = activeIndexRef.current

    if (nextActive === null) {
      if (prevActive !== null) {
        getHandle(prevActive)?.pause()
        activeIndexRef.current = null
      }
      return
    }

    if (prevActive !== null && prevActive !== nextActive) {
      getHandle(prevActive)?.pause()
    }

    activeIndexRef.current = nextActive
    if (prevActive !== nextActive || forceReplayIndex === nextActive) {
      getHandle(nextActive)?.play()
    }
  }, [getHandle, sortedInView])

  const bindVideoRef = useCallback((index: number, el: HTMLVideoElement | null) => {
    if (el) {
      videoMapRef.current.set(index, el)
    } else {
      videoMapRef.current.delete(index)
      inViewSetRef.current.delete(index)
      clearStartTimer(index)
    }
  }, [clearStartTimer])

  const onInViewChange = useCallback((index: number, inView: boolean) => {
    clearStartTimer(index)

    if (inView) {
      inViewSetRef.current.add(index)
      const timer = setTimeout(() => {
        startTimersRef.current.delete(index)
        if (!inViewSetRef.current.has(index)) return
        selectAndPlay(index)
      }, anchorIdleDelayMs)
      startTimersRef.current.set(index, timer)
    } else {
      inViewSetRef.current.delete(index)
      getHandle(index)?.pause()
      if (activeIndexRef.current === index) {
        activeIndexRef.current = null
        selectAndPlay()
      }
    }
  }, [anchorIdleDelayMs, clearStartTimer, getHandle, selectAndPlay])

  const onEnded = useCallback((endedIndex: number) => {
    const sorted = sortedInView()
    const pos = sorted.indexOf(endedIndex)
    if (pos === -1 || sorted.length === 0) return

    const nextPos = (pos + 1) % sorted.length
    const nextIndex = sorted[nextPos]
    activeIndexRef.current = nextIndex
    getHandle(nextIndex)?.play()
  }, [getHandle, sortedInView])

  const retryActive = useCallback(() => {
    selectAndPlay(activeIndexRef.current ?? undefined)
  }, [selectAndPlay])

  useEffect(() => {
    if (!canAutoplay) {
      clearAllTimers()
      videoMapRef.current.forEach((el) => releaseVideo(el))
      activeIndexRef.current = null
      inViewSetRef.current.clear()
    } else {
      selectAndPlay()
    }
  }, [canAutoplay, resumeKey, clearAllTimers, selectAndPlay])

  useEffect(() => {
    const videoMap = videoMapRef.current
    return () => {
      clearAllTimers()
      videoMap.forEach((el) => el.pause())
    }
  }, [clearAllTimers])

  const reset = useCallback(() => {
    inViewSetRef.current.clear()
    clearAllTimers()
    if (activeIndexRef.current !== null) {
      getHandle(activeIndexRef.current)?.pause()
      activeIndexRef.current = null
    }
  }, [clearAllTimers, getHandle])

  return { bindVideoRef, onInViewChange, onEnded, retryActive, reset }
}
